import React, { useState } from 'react';
import { X, DollarSign, MessageCircle } from 'lucide-react';

const MakeOfferModal = ({ isOpen, onClose, domain }) => {
  const [offerAmount, setOfferAmount] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !domain) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!offerAmount || parseFloat(offerAmount) <= 0) {
      alert('Please enter a valid offer amount');
      return;
    }

    setIsSubmitting(true);

    try {
      // TODO: Implement offer submission to Firestore
      // For now, just show a success message
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call

      alert(`Offer submitted for ${domain.name}!\n\nAmount: $${offerAmount}\nMessage: ${message || 'No message'}\n\n(Feature coming soon - offers will be sent to domain owners)`);

      onClose();
      setOfferAmount('');
      setMessage('');
    } catch (error) {
      console.error('Failed to submit offer:', error);
      alert('Failed to submit offer. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isAvailable = domain.owner === 'Available';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-800">
          <div>
            <h2 className="text-2xl font-bold text-white">
              {isAvailable ? '🎯 Buy Domain' : '💰 Make an Offer'}
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              {isAvailable ? 'Register this domain before someone else does!' : 'Negotiate with the owner'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Domain Info */}
        <div className="p-6 bg-slate-950/50 border-b border-slate-800">
          <div className="font-mono text-xl text-amber-300 font-bold mb-2">
            {domain.name}
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-slate-400">
              Current Price: <span className="text-white font-bold">{domain.price}</span>
            </span>
            {!isAvailable && (
              <span className="text-slate-500">
                Owner: <span className="text-slate-300">{domain.owner}</span>
              </span>
            )}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-6">
            <label className="block text-sm font-bold text-slate-300 mb-2">
              <DollarSign className="w-4 h-4 inline mr-1" />
              Your Offer Amount
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg">$</span>
              <input
                type="number"
                value={offerAmount}
                onChange={(e) => setOfferAmount(e.target.value)}
                placeholder="1000"
                min="1"
                step="1"
                required
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-10 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 transition"
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {isAvailable ? 'Enter your budget for registration and setup' : 'Make your best offer'}
            </p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-bold text-slate-300 mb-2">
              <MessageCircle className="w-4 h-4 inline mr-1" />
              Message {isAvailable ? '(Optional)' : 'to Owner'}
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={isAvailable ? "Why do you want this domain?" : "Convince the owner to sell..."}
              rows="4"
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 transition resize-none"
            />
            <p className="text-xs text-slate-500 mt-2">
              {message.length}/500 characters
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-3 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : isAvailable ? '💸 Buy Domain' : '🤝 Submit Offer'}
            </button>
          </div>

          <p className="text-xs text-center text-slate-600 mt-4">
            {isAvailable
              ? 'Domain registration services coming soon!'
              : 'Offers are sent directly to the domain owner'
            }
          </p>
        </form>
      </div>
    </div>
  );
};

export default MakeOfferModal;
